/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Tracks the current state of the Shaka Player globally
 * Used to persist the current time and playing state when switching streams
 */
const ShakaInstanceTracker = {
  currentStream: "",
  currentTime: 0,
  currentWasPlaying: false
};
const APPEND_CMAF_FORMAT = stream => `${stream}(format=m3u8-cmaf)`;
/**
 * Used to interact with the Shaka Player and load a stream from MediaValet Assets
 *
 * Requires the Shaka Player to be loaded on the page before calling any methods
 */
class MvShakaPlayer {
  constructor(logger, shaka) {
    this.logger = logger;
    this.shaka = shaka;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ShakaPlayer is a shaka object and no good typings exist
    this.player = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ShakaControls is a shaka object and no good typings exist
    this.controls = null;
    this.onPlayheadTimeUpdate = null;
    this.onDurationAvailable = null;
    this.initAms = ({
      stream,
      videoContainerElement,
      videoElement
    }) => {
      const transformedStream = APPEND_CMAF_FORMAT(stream);
      return this.init({
        stream: transformedStream,
        videoContainerElement,
        videoElement
      });
    };
    /**
     * Loads a stream from AMS into the player
     * @deprecated Use loadStream instead - AMS streams are no longer available
     * @param stream
     * @param videoContainerElement
     * @param videoElement
     * @param uiConfig - The UI configuration for the player - see https://shaka-player-demo.appspot.com/docs/api/tutorial-ui-customization.html
     * @param playerConfig - The player configuration for the player - see https://shaka-player-demo.appspot.com/docs/api/tutorial-config.html
     */
    this.loadStreamAms = ({
      stream,
      videoContainerElement,
      videoElement,
      playerConfig = {},
      uiConfig = {}
    }) => {
      const transformedStream = APPEND_CMAF_FORMAT(stream);
      return this.loadStream({
        stream: transformedStream,
        videoContainerElement,
        videoElement,
        playerConfig,
        uiConfig
      });
    };
    /**
     * Initializes the Shaka Player and attaches it to the video element
     * @param stream - The stream URL to load from MediaValet
     * @param videoContainerElement - The container element to attach the player to
     * @param videoElement - The video element to attach the player to
     * @param uiConfig - The UI configuration for the player - see https://shaka-player-demo.appspot.com/docs/api/tutorial-ui-customization.html
     * @param playerConfig - The player configuration for the player - see https://shaka-player-demo.appspot.com/docs/api/tutorial-config.html
     */
    this.init = ({
      stream,
      videoContainerElement,
      videoElement,
      uiConfig = {},
      playerConfig = {}
    }) => __awaiter(this, void 0, void 0, function* () {
      // we want to disable the picture in picture functionality for the video element
      videoElement.disablePictureInPicture = true;
      // "local" because it is for local playback only, as opposed to the player proxy
      // object, which will route your calls to the ChromeCast receiver as necessary.
      const localPlayer = new this.shaka.Player();
      // "Overlay" because the UI will add DOM elements inside the container,
      // to visually overlay the video element
      const ui = new this.shaka.ui.Overlay(localPlayer, videoContainerElement, videoElement);
      ui.configure(Object.assign({
        enableTooltips: true,
        overflowMenuButtons: ["captions", "quality", "playback_rate"]
      }, uiConfig));
      // Now that the player has been configured to be part of a UI, attach it to the
      // video.
      yield localPlayer.attach(videoElement);
      if (!ui) {
        this.logger.error("UI not loaded");
        return;
      }
      this.controls = ui.getControls();
      this.player = this.controls.getPlayer();
      // Listen for error events.
      this.player.addEventListener("error", this.onPlayerErrorEvent);
      this.controls.addEventListener("error", this.onUIErrorEvent);
      this.player.addEventListener("statechanged", this.onStateChangedEvent);
      this.player.addEventListener("timeandseekrangeupdated", this.onTimeAndSeekRangeUpdatedEvent);
      this.player.configure(Object.assign({
        abr: {
          enabled: true
        }
      }, playerConfig));
      yield this.loadStreamInternal(stream);
    });
    /**
     * Loads a stream into the player, does not reinitialize the player if it has already been initialized
     * @param stream - The stream URL to load from MediaValet
     * @param videoContainerElement - The container element to attach the player to
     * @param videoElement - The video element to attach the player to
     * @param uiConfig - The UI configuration for the player - see https://shaka-player-demo.appspot.com/docs/api/tutorial-ui-customization.html
     * @param playerConfig - The player configuration for the player - see https://shaka-player-demo.appspot.com/docs/api/tutorial-config.html
     */
    this.loadStream = ({
      stream,
      videoContainerElement,
      videoElement,
      uiConfig = {},
      playerConfig = {}
    }) => __awaiter(this, void 0, void 0, function* () {
      // if has initialized
      // then you can load
      // else you need to wait for the init to complete
      if (!this.player) {
        yield this.init({
          stream,
          videoContainerElement,
          videoElement,
          uiConfig,
          playerConfig
        });
        return;
      }
      yield this.loadStreamInternal(stream);
    });
    this.destroy = () => {
      var _a, _b, _c, _d, _e, _f, _g;
      ShakaInstanceTracker.currentTime = (_a = this.player) === null || _a === void 0 ? void 0 : _a.getMediaElement().currentTime;
      ShakaInstanceTracker.currentWasPlaying = !((_b = this.player) === null || _b === void 0 ? void 0 : _b.getMediaElement().paused);
      this.logger.log("MvShakaPlayer::current time", ShakaInstanceTracker.currentTime);
      (_c = this.player) === null || _c === void 0 ? void 0 : _c.removeEventListener("error", this.onPlayerErrorEvent);
      (_d = this.controls) === null || _d === void 0 ? void 0 : _d.removeEventListener("error", this.onUIErrorEvent);
      (_e = this.controls) === null || _e === void 0 ? void 0 : _e.removeEventListener("statechanged", this.onStateChangedEvent);
      (_f = this.controls) === null || _f === void 0 ? void 0 : _f.removeEventListener("timeandseekrangeupdated", this.onTimeAndSeekRangeUpdatedEvent);
      (_g = this.player) === null || _g === void 0 ? void 0 : _g.destroy();
      this.player = null;
      this.controls = null;
      this.logger.info("MvShakaPlayer::cleaned up");
    };
    this.onPlayerError = error => {
      // Handle player error
      this.logger.error("Error code", error.code, "object", error);
    };
    this.onPlayerErrorEvent = errorEvent => {
      // Extract the shaka.util.Error object from the event.
      this.onPlayerError(errorEvent.detail);
    };
    this.onUIErrorEvent = errorEvent => {
      // Extract the shaka.util.Error object from the event.
      this.onPlayerError(errorEvent.detail);
    };
    this.onError = error => {
      // Log the error.
      this.logger.error("Error code", error.code, "object", error);
    };
    this.onStateChangedEvent = event => {
      this.logger.info("State change event triggered");
      this.handlePlayheadTimeUpdate(event);
    };
    this.onTimeAndSeekRangeUpdatedEvent = event => {
      this.logger.info("Time and seek range updated event triggered");
      this.handlePlayheadTimeUpdate(event);
    };
  }
  handlePlayheadTimeUpdate(event) {
    var _a, _b;
    const currentTime = (_b = (_a = event.currentTarget) === null || _a === void 0 ? void 0 : _a.getMediaElement()) === null || _b === void 0 ? void 0 : _b.currentTime;
    if (this.onPlayheadTimeUpdate) {
      this.onPlayheadTimeUpdate(currentTime || 0);
    }
  }
  loadStreamInternal(stream) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
      // Try to load a manifest.
      // This is an asynchronous process.
      try {
        const streamUrlWithoutToken = stream.split("?")[0];
        const sameVideoLoad = ShakaInstanceTracker.currentStream === streamUrlWithoutToken;
        ShakaInstanceTracker.currentStream = streamUrlWithoutToken;
        yield this.player.load(stream);
        this.logger.log("MvShakaPlayer::video loaded", stream);
        if (sameVideoLoad && ShakaInstanceTracker.currentTime > 0) {
          this.logger.log("MvShakaPlayer::attempting to seek to previous time and play, because same video was loaded");
          this.player.getMediaElement().currentTime = ShakaInstanceTracker.currentTime;
          if (ShakaInstanceTracker.currentWasPlaying) {
            yield this.player.getMediaElement().play();
          }
        }
        if (this.onDurationAvailable) {
          // Update the video duration
          const duration = (_a = this.player.getMediaElement()) === null || _a === void 0 ? void 0 : _a.duration;
          this.onDurationAvailable(duration);
          this.logger.info("Duration", duration);
        }
      } catch (e) {
        // onError is executed if the asynchronous load fails.
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        this.onError(e);
      }
    });
  }
}

export { MvShakaPlayer };
